import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause } from 'lucide-react';

// Videos live in /public and are fetched only once a card is played. Posters are
// pre-generated stills (see public/vidN-poster.webp) - deriving them in the
// browser meant streaming every clip just to paint a single frame.
const VIDEO_WIDTH = 368;
const VIDEO_HEIGHT = 640;

// --- LUXURY VIDEO CARD ---
const VideoCard = ({ video, isActive, onPlay }) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (isActive) {
      onPlay(null); // Pause
    } else {
      onPlay(video.id); // Play
    }
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (isActive) {
      // Attach the source on first play only - this is what keeps ~19MB of
      // video off the homepage load.
      if (!el.src) {
        el.src = video.src;
        el.load();
      }
      el.play().catch(e => console.log("Autoplay prevented", e));
    } else {
      el.pause();
    }
  }, [isActive, video.src]);

  return (
    <div className="group flex flex-col gap-4">
      {/* Video Container (Sharp, 9:16 Portrait) */}
      <div 
        className="relative aspect-[9/16] bg-gray-100 overflow-hidden cursor-pointer"
        onClick={togglePlay}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          ref={videoRef}
          preload="none"
          className={`w-full h-full object-cover transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0'}`}
          loop
          muted={false} // Allow sound if user clicks
          playsInline
        />

        {/* Poster Layer */}
        {!isActive && (
          <div className="absolute inset-0">
             <img
               src={video.poster}
               alt={video.title}
               width={VIDEO_WIDTH}
               height={VIDEO_HEIGHT}
               loading="lazy"
               decoding="async"
               className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
             />

             {/* Minimal Overlay */}
             <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300" />
          </div>
        )}

        {/* Center Control (Minimal) */}
        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${isActive && !isHovered ? 'opacity-0' : 'opacity-100'}`}>
          <div className="w-12 h-12 flex items-center justify-center backdrop-blur-md bg-white/10 border border-white/20 text-white rounded-full">
            {isActive ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" className="ml-1" />}
          </div>
        </div>

        {/* Duration Badge (Top Right) */}
        <div className="absolute top-4 right-4 text-[9px] font-medium text-white/90 uppercase tracking-widest">
          {video.duration}
        </div>
      </div>

      {/* Caption Section (Editorial Style) */}
      <div className="space-y-1">
        <h3 className="text-sm font-serif italic text-black">
          {video.title}
        </h3>
        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
          {video.overlayText}
        </p>
      </div>
    </div>
  );
};

// --- MAIN SECTION ---
const VideoCardSection = () => {
  const [activeId, setActiveId] = useState(null);

  const videos = [
    {
      id: 1,
      src: "/videos/vid1.mp4",
      poster: "/vid1-poster.webp",
      title: "Morning Rituals",
      duration: "0:45",
      overlayText: "The Art of Layering"
    },
    {
      id: 2,
      src: "/videos/vid2.mp4",
      poster: "/vid2-poster.webp",
      title: "Discovery Set",
      duration: "0:30",
      overlayText: "Find Your Signature"
    },
    {
      id: 3,
      src: "/videos/vid3.mp4",
      poster: "/vid3-poster.webp",
      title: "Application",
      duration: "0:25",
      overlayText: "Pulse Points Guide"
    },
    {
      id: 4,
      src: "/videos/vid4.mp4",
      poster: "/vid4-poster.webp",
      title: "Evening Wear",
      duration: "0:55",
      overlayText: "Scent Transitions"
    }
  ];

  return (
    <section className="bg-white py-24 border-t border-black">
      
      {/* Invisible Grid Container */}
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12">
        
        {/* Header */}
        <div className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-4">
              Visual Notes
            </span>
            <h2 className="text-4xl md:text-6xl font-serif italic text-black leading-none">
              Scent in <span className="not-italic font-light tracking-tighter">Motion</span>
            </h2>
          </div>
          {/* <div className="hidden md:block pb-2">
            <a href="#" className="text-[10px] uppercase tracking-[0.2em] border-b border-black pb-1 hover:text-gray-500 hover:border-gray-500 transition-colors">
              View All Stories
            </a>
          </div> */}
        </div>

        {/* Grid Layout (Desktop: 4 cols, Mobile: Scroll) */}
        <div className="relative">
          <div className="flex overflow-x-auto pb-8 snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 scrollbar-hide">
            {videos.map((video) => (
              <div key={video.id} className="min-w-[85vw] md:min-w-0 snap-center">
                <VideoCard 
                  video={video} 
                  isActive={activeId === video.id}
                  onPlay={setActiveId}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile View All Link */}
        {/* <div className="md:hidden text-center mt-8">
           <a href="#" className="text-[10px] uppercase tracking-[0.2em] border-b border-black pb-1">
              View All Stories
            </a>
        </div> */}

      </div>
    </section>
  );
};

export default VideoCardSection;